import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, X, Phone, Mail, RefreshCw, Sparkles } from 'lucide-react';
import { useChatContext } from '../context/ChatContext';

const ALL_FIELDS = ['service', 'location', 'date', 'volume', 'reach', 'phone', 'email'];
const REQUIRED_FIELDS = ['service'];
const CONTACT_FIELDS = ['phone', 'email'];
const OPTIONAL_FIELDS = ['location', 'date', 'volume', 'reach'];

const QUICK_STARTS = [
  '👷‍♀️ Betonpumpe mieten',
  '🚛 Beton liefern lassen',
  '🏗️ Beton kaufen',
];

const FALLBACK_STEPS = {
  service: {
    question: 'Ich bin Lisa von MH-BETON. Brauchst du Beton für eine Baustelle in Wien oder Umgebung? Was brauchst du – Pumpe mieten, Beton liefern lassen, Beton kaufen?',
    options: ['Betonpumpe mit Fahrer', 'Beton liefern lassen', 'Beton kaufen (mit Transport & Pumpe)', 'Alles zusammen'],
    next: 'contact',
  },
  contact: {
    question: 'Super, danke. 👍 Wie kann MH-BETON dich am besten erreichen – Telefon oder E-Mail reicht.',
    placeholder: 'z. B. +43 660 123 4567 oder max@muster.at',
    next: 'summary',
  },
};

const OPTIONAL_PROMPTS = [
  { field: 'location', question: 'Wo liegt die Baustelle? PLZ oder Ort reicht völlig.', placeholder: 'z. B. 1230 Wien' },
  { field: 'date', question: 'Wann soll der Beton ungefähr kommen?', placeholder: 'z. B. morgen, diese Woche, nächste Woche' },
  { field: 'volume', question: 'Welche Menge hast du im Kopf? Wenn du unsicher bist, sag grob – MH-BETON hilft bei der Feinabstimmung.', placeholder: 'z. B. 10 m³, Bodenplatte, noch unklar' },
  { field: 'reach', question: 'Gibt es etwas zur Zufahrt? Enger Hof, langer Pumpweg, schwierige Anfahrt?', placeholder: 'z. B. enge Straße, Hinterhof, 20 m Pumpweg' },
];

function buildInquiryPayload(state) {
  return {
    service: state.service || '',
    location: state.location || '',
    date: state.date || '',
    volume: state.volume || '',
    reach: state.reach || '',
    phone: state.phone || '',
    email: state.email || '',
  };
}

function detectContactType(value) {
  const emailRegex = /@[\w.-]+\.[a-z]{2,}/i;
  const phoneRegex = /[\d+][\d\s/()-]{4,}/;
  if (emailRegex.test(value)) return 'email';
  if (phoneRegex.test(value)) return 'phone';
  return null;
}

function ChatWidget() {
  const { isOpen, toggleChat, closeChat } = useChatContext();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [data, setData] = useState({
    service: '', location: '', date: '', volume: '', reach: '', phone: '', email: '',
  });
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [aiMode, setAiMode] = useState(null);
  const [fbStep, setFbStep] = useState('service');
  const [optionalStep, setOptionalStep] = useState(0);
  const [collectingOptional, setCollectingOptional] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatRef = useRef(null);
  const aiMessagesRef = useRef([]);

  const requiredCount = REQUIRED_FIELDS.filter((f) => data[f]).length;
  const hasContact = CONTACT_FIELDS.some((f) => data[f]);
  const optionalCount = OPTIONAL_FIELDS.filter((f) => data[f]).length;
  const isComplete = data.service && hasContact;
  const progressTotal = REQUIRED_FIELDS.length + 1 + OPTIONAL_FIELDS.length;
  const progressCurrent = requiredCount + (hasContact ? 1 : 0) + optionalCount;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, isTyping, typingText]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeChat();
      if (e.key === 'Tab' && chatRef.current) {
        const focusable = chatRef.current.querySelectorAll('button, a, input, [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closeChat]);

  const addBotMessage = useCallback((text, type = 'bot') => {
    setMessages((prev) => [...prev, { type, text }]);
  }, []);

  const addUserMessage = useCallback((text) => {
    setMessages((prev) => [...prev, { type: 'user', text }]);
  }, []);

  const startConversation = useCallback(async () => {
    if (started) return;
    setStarted(true);

    addBotMessage('Hallo, ich bin Lisa von MH-BETON. Brauchst du Beton für eine Baustelle in Wien oder Umgebung?');

    aiMessagesRef.current = [
      { role: 'assistant', content: 'Hallo, ich bin Lisa von MH-BETON. Brauchst du Beton für eine Baustelle in Wien oder Umgebung?' },
    ];

    setAiMode(true);
  }, [started, addBotMessage]);

  useEffect(() => {
    if (isOpen && !started) {
      startConversation();
    }
  }, [isOpen, started, startConversation]);

  const typeWithDelay = useCallback((text) => {
    return new Promise((resolve) => {
      setLoading(false);
      setIsTyping(true);
      setTypingText('');
      const words = text.split(' ');
      let i = 0;
      const interval = setInterval(() => {
        if (i >= words.length) {
          clearInterval(interval);
          setTimeout(() => {
            setIsTyping(false);
            setTypingText('');
            addBotMessage(text);
            resolve();
          }, 300);
          return;
        }
        setTypingText((prev) => (prev ? prev + ' ' : '') + words[i]);
        i++;
      }, 55);
    });
  }, [addBotMessage]);

  const handleAiMessage = async (userText) => {
    addUserMessage(userText);
    aiMessagesRef.current = [...aiMessagesRef.current, { role: 'user', content: userText }];

    setLoading(true);
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: aiMessagesRef.current }),
      });
      const result = await response.json();

      if (result.fallback) {
        setAiMode(false);
        setLoading(false);
        addBotMessage('Ich bin Lisa von MH-BETON. Brauchst du Beton für eine Baustelle in Wien oder Umgebung? Was brauchst du – Pumpe, Lieferung, Beton kaufen?');
        setFbStep('service');
        return;
      }

      if (result.data) {
        setData((prev) => {
          const updated = { ...prev };
          ALL_FIELDS.forEach((field) => {
            if (result.data[field] && !updated[field]) {
              updated[field] = result.data[field];
            }
          });
          return updated;
        });
      }

      let reply = result.reply || '';
      if (!reply.trim()) {
        reply = 'Alles klar, notiert. Was kann ich noch für dich tun?';
      }
      aiMessagesRef.current = [...aiMessagesRef.current, { role: 'assistant', content: reply }];
      await typeWithDelay(reply);
    } catch {
      setAiMode(false);
      setLoading(false);
      addBotMessage('Ups, kurzes Verbindungsproblem. Was brauchst du für deine Baustelle?');
      setFbStep('service');
    }
  };

  const handleFallbackAnswer = (value) => {
    if (!value.trim()) return;

    if (collectingOptional) {
      const field = OPTIONAL_PROMPTS[optionalStep - 1]?.field;
      if (field) {
        setData((prev) => ({ ...prev, [field]: value }));
      }
      addUserMessage(value);
      advanceOptional();
      return;
    }

    const currentStep = fbStep;
    const stepConfig = FALLBACK_STEPS[currentStep];
    if (!stepConfig) return;

    if (currentStep === 'contact') {
      const contactType = detectContactType(value);
      const updated = { ...data };
      if (contactType === 'email') {
        updated.email = value;
      } else if (contactType === 'phone') {
        updated.phone = value;
      } else {
        updated.phone = value;
      }
      setData(updated);
      addUserMessage(value);
      showFallbackSummary(updated);
      return;
    }

    setData((prev) => ({ ...prev, [currentStep]: value }));
    addUserMessage(value);

    const nextStep = stepConfig.next;
    if (nextStep === 'summary') {
      showFallbackSummary({ ...data, [currentStep]: value });
    } else {
      setFbStep(nextStep);
      const nextConfig = FALLBACK_STEPS[nextStep];
      if (nextConfig) {
        setTimeout(() => addBotMessage(nextConfig.question), 300);
      }
    }
  };

  const advanceOptional = () => {
    if (optionalStep < OPTIONAL_PROMPTS.length) {
      const next = optionalStep + 1;
      setOptionalStep(next);
      const prompt = OPTIONAL_PROMPTS[next - 1];
      if (prompt) {
        setTimeout(() => addBotMessage(prompt.question), 300);
      }
    } else {
      setCollectingOptional(false);
      setTimeout(() => addBotMessage('Danke für die Details! Soll ich die Anfrage jetzt an MH-BETON senden?'), 300);
    }
  };

  const showFallbackSummary = (finalData) => {
    setFbStep('done');
    const summaryParts = [`Leistung: ${finalData.service || '-'}`];
    if (finalData.location) summaryParts.push(`Ort: ${finalData.location}`);
    if (finalData.date) summaryParts.push(`Termin: ${finalData.date}`);
    if (finalData.volume) summaryParts.push(`Menge: ${finalData.volume}`);
    if (finalData.reach) summaryParts.push(`Zufahrt: ${finalData.reach}`);
    if (finalData.phone) summaryParts.push(`Tel: ${finalData.phone}`);
    if (finalData.email) summaryParts.push(`E-Mail: ${finalData.email}`);
    addBotMessage(summaryParts.join(' | '), 'summary');
    addBotMessage('Passt, ich hab alles was MH-BETON braucht. ✅ Soll ich die Anfrage so abschicken oder gibt es noch etwas zur Zufahrt oder besonderen Anforderungen?');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value || loading || sending) return;
    setInput('');
    if (aiMode === true) {
      handleAiMessage(value);
    } else {
      handleFallbackAnswer(value);
    }
  };

  const handleQuickStart = (text) => {
    if (loading || sending) return;
    setInput('');
    if (aiMode === true) {
      handleAiMessage(text);
    } else {
      handleFallbackAnswer(text);
    }
  };

  const restart = () => {
    setMessages([]);
    setData({ service: '', location: '', date: '', volume: '', reach: '', phone: '', email: '' });
    setSent(false);
    setSendError(null);
    setSending(false);
    setFbStep('service');
    setOptionalStep(0);
    setCollectingOptional(false);
    aiMessagesRef.current = [];
    setStarted(false);
    startConversation();
  };

  const startOptionalCollection = () => {
    setCollectingOptional(true);
    setOptionalStep(1);
    const prompt = OPTIONAL_PROMPTS[0];
    if (prompt) {
      addBotMessage(`Gerne! Eine weitere Frage: ${prompt.question}`);
    }
  };

  const sendInquiry = async () => {
    setSending(true);
    setSendError(null);
    try {
      const response = await fetch('/api/send-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: buildInquiryPayload(data) }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Senden fehlgeschlagen');
      }
      setSent(true);
      addBotMessage('Vielen Dank für deine Anfrage! Ich hab alles an MH-BETON weitergegeben. Sie rufen dich an oder antworten dir per E-Mail – meist innerhalb von 2-3 Stunden an Werktagen, spätestens am nächsten Vormittag.');
      setTimeout(() => addBotMessage('Mein Name ist Lisa und ich stehe dir jederzeit wieder zur Verfügung. Schreib einfach, wenn du noch was brauchst. 👍'), 600);
    } catch (err) {
      setSendError(err.message || 'E-Mail konnte nicht gesendet werden.');
      addBotMessage('Beim Senden ist was schiefgelaufen. Du kannst die Anfrage auch direkt per E-Mail oder telefonisch stellen – +43 660 909 1992.');
    } finally {
      setSending(false);
    }
  };

  const showSendButton = isComplete && !sent;
  const fbStepConfig = FALLBACK_STEPS[fbStep];
  const showQuickStarts = aiMode === true && messages.length <= 1 && !loading;
  const showOptionalButton = (aiMode === false && fbStep === 'done' && optionalStep === 0 && !collectingOptional) || (aiMode === true && isComplete && !collectingOptional && optionalStep === 0);

  return (
    <div className="fixed right-4 bottom-4 z-[70] flex flex-col items-end gap-2">
      {isOpen && (
        <section
          ref={chatRef}
          className="w-[min(480px,calc(100vw-2rem))] max-h-[min(760px,calc(100svh-2rem))] rounded-card border border-signal/25 bg-[#0b2028] text-chat-ink shadow-[0_30px_80px_rgba(13,37,46,0.32)] overflow-hidden flex flex-col"
          style={{
            background: 'radial-gradient(circle at 100% 0%, rgba(50, 185, 232, 0.2), transparent 12rem), #0b2028',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="MH-BETON Baustellen-Assistent"
        >
          <header className="flex items-center justify-between gap-4 p-4 bg-white/5">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-xl border border-signal/40 bg-signal/10 text-signal text-sm font-black">MH</span>
              <div>
                <strong className="block text-sm font-bold text-white">Lisa · Baustellen-Assistent</strong>
                <span className="flex items-center gap-1.5 text-xs text-chat-muted">
                  {aiMode === true && <><Sparkles className="w-3 h-3 text-signal" /> KI-gestützt · Online</>}
                  {aiMode === false && <>Online · fragt nur, was für die Rückmeldung zählt.</>}
                  {aiMode === null && <>Verbinde…</>}
                </span>
              </div>
            </div>
            <button
              onClick={closeChat}
              className="w-10 h-10 rounded-full border border-white/20 text-white grid place-items-center hover:bg-white/10"
              aria-label="Chat schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </header>

          <div className="flex items-center gap-1.5 px-4 pt-3 bg-white/5" role="progressbar" aria-label={`${progressCurrent} von ${progressTotal} Angaben erfasst`} aria-valuenow={progressCurrent} aria-valuemin="0" aria-valuemax={progressTotal}>
            {REQUIRED_FIELDS.map((field) => (
              <span key={field} className={`h-1 flex-1 rounded-full transition-all duration-500 ${data[field] ? 'bg-signal-light shadow-[0_0_14px_rgba(50,185,232,0.36)]' : 'bg-white/15'}`} />
            ))}
            <span className={`h-1 flex-1 rounded-full transition-all duration-500 ${hasContact ? 'bg-signal-light shadow-[0_0_14px_rgba(50,185,232,0.36)]' : 'bg-white/15'}`} />
            {OPTIONAL_FIELDS.map((field, index) => (
              <span key={field} className={`h-1 flex-1 rounded-full transition-all duration-500 ${data[field] ? 'bg-signal/60' : 'bg-white/10'}`} style={{ transitionDelay: `${index * 40}ms` }} />
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`max-w-[88%] px-3.5 py-2.5 rounded-lg text-sm leading-relaxed ${
                  message.type === 'user'
                    ? 'ml-auto bg-gradient-to-br from-chat-accent to-chat-accent-strong text-white font-bold'
                    : message.type === 'summary'
                    ? 'w-full max-w-full border border-signal/20 bg-chat-surface-soft text-chat-ink'
                    : 'bg-chat-surface text-chat-ink border border-white/10'
                }`}
              >
                {message.text}
              </div>
            ))}
            {loading && (
              <div className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg bg-chat-surface text-chat-ink border border-white/10">
                <span className="w-2 h-2 rounded-full bg-signal animate-typing-dot" />
                <span className="w-2 h-2 rounded-full bg-signal animate-typing-dot [animation-delay:150ms]" />
                <span className="w-2 h-2 rounded-full bg-signal animate-typing-dot [animation-delay:300ms]" />
              </div>
            )}
            {isTyping && (
              <div className="max-w-[88%] px-3.5 py-2.5 rounded-lg bg-chat-surface text-chat-ink border border-white/10 text-sm leading-relaxed">
                {typingText}
                <span className="inline-block w-0.5 h-3.5 ml-0.5 bg-signal animate-pulse align-text-bottom" />
              </div>
            )}
            {sendError && <div className="text-xs text-red-500 bg-red-950/50 p-2 rounded">{sendError}</div>}
            <div ref={messagesEndRef} />
          </div>

          {showQuickStarts && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {QUICK_STARTS.map((text) => (
                <button
                  key={text}
                  onClick={() => handleQuickStart(text)}
                  className="px-3 py-1.5 min-h-[44px] rounded-full border border-white/20 bg-white/10 text-chat-ink text-sm font-bold hover:bg-chat-accent/40 hover:border-white/40 active:scale-[0.97] transition-all"
                >
                  {text}
                </button>
              ))}
            </div>
          )}

          {aiMode === false && fbStepConfig?.options && fbStep !== 'done' && !collectingOptional && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {fbStepConfig.options.map((option) => (
                <button
                  key={option}
                  onClick={() => handleFallbackAnswer(option)}
                  className="px-3 py-1.5 min-h-[44px] rounded-full border border-white/20 bg-white/10 text-chat-ink text-sm font-bold hover:bg-chat-accent/40 hover:border-white/40 active:scale-[0.97] transition-all"
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {showSendButton && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              <button
                onClick={sendInquiry}
                disabled={sending}
                className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-full bg-gradient-to-r from-signal to-[#63d0f3] text-ink text-sm font-extrabold border border-white/20 shadow-[0_8px_24px_rgba(50,185,232,0.28)] hover:shadow-[0_12px_32px_rgba(50,185,232,0.36)] hover:-translate-y-0.5 active:scale-[0.97] transition-all disabled:opacity-60 disabled:hover:translate-y-0 disabled:active:scale-100"
              >
                {sending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-ink/30 border-t-ink rounded-full animate-spin" />
                    Wird gesendet…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Anfrage senden
                  </>
                )}
              </button>
              {showOptionalButton && (
                <button
                  onClick={startOptionalCollection}
                  className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-full border border-white/20 bg-white/10 text-chat-ink text-sm font-bold hover:bg-chat-accent/40 active:scale-[0.97] transition-all"
                >
                  Details ergänzen
                </button>
              )}
              <a
                href="tel:+436609091992"
                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-full border border-white/20 bg-white/10 text-chat-ink text-sm font-bold hover:bg-chat-accent/40 active:scale-[0.97] transition-all"
              >
                <Phone className="w-4 h-4" />
                Anrufen
              </a>
              {sendError && (
                <a
                  href={`mailto:office@mh-beton.at?subject=${encodeURIComponent(`Anfrage ${data.service || 'MH-BETON'} - ${data.location || ''}`)}&body=${encodeURIComponent(`Leistung: ${data.service}\nOrt: ${data.location}\nTermin: ${data.date}\nMenge: ${data.volume}\nZufahrt: ${data.reach}\nTelefon: ${data.phone}\nE-Mail: ${data.email}`)}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-full border border-white/20 bg-white/10 text-chat-ink text-sm font-bold hover:bg-chat-accent/40 transition-all"
                >
                  <Mail className="w-4 h-4" />
                  E-Mail-App
                </a>
              )}
              <button
                onClick={restart}
                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-full border border-white/10 bg-white/5 text-chat-muted text-sm font-bold hover:bg-white/10 hover:text-chat-ink active:scale-[0.97] transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                Neu starten
              </button>
            </div>
          )}

          {sent && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              <button
                onClick={restart}
                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-full border border-white/10 bg-white/5 text-chat-muted text-sm font-bold hover:bg-white/10 hover:text-chat-ink active:scale-[0.97] transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                Neue Anfrage
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-3 bg-white/5 flex gap-2">
            <input
              ref={inputRef}
              type={collectingOptional || fbStep === 'contact' ? 'text' : 'text'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={aiMode === true ? 'Nachricht an Lisa…' : collectingOptional ? OPTIONAL_PROMPTS[optionalStep - 1]?.placeholder || 'Antwort eingeben' : fbStepConfig?.placeholder || 'Antwort eingeben'}
              className="flex-1 min-h-[46px] px-3 rounded-md border border-white/15 bg-white/90 text-base text-ink focus:outline focus:outline-2 focus:outline-signal"
              autoComplete="off"
              aria-label="Nachricht eingeben"
            />
            <button
              type="submit"
              className="min-h-[46px] px-4 rounded-md bg-gradient-to-br from-chat-accent to-chat-accent-strong text-white font-black hover:opacity-90"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </section>
      )}

      <button
        onClick={toggleChat}
        className="relative flex flex-col justify-center min-w-[176px] max-w-[calc(100vw-2rem)] min-h-[58px] px-4 rounded-card border border-signal/30 bg-gradient-to-br from-chat-accent to-chat-accent-strong text-white shadow-elevated hover:-translate-y-0.5 transition-transform text-left"
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Chat schließen' : 'Assistent öffnen'}
      >
        <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_0_5px_rgba(32,201,151,0.18)]" />
        <span className="text-[0.74rem] font-black uppercase text-chat-ink/80">Assistent</span>
        <strong className="text-base font-bold">Projekt klären</strong>
      </button>
    </div>
  );
}

export default ChatWidget;
